import { useEffect, useId, useState } from 'react';
import { SIGNAL } from '@continuum/show-core';
import { PrimaryButton, mono, sans } from '../ui/atoms.js';
import { BottomSheet, useSheet } from '../ui/BottomSheet.js';
import { TopBanner } from '../ui/TopBanner.js';
import { PRIORITY, useBanner } from '../ui/OverlayHost.js';
import type { Lang } from '../ui/text.js';

/**
 * The hidden signal (show-core `signal.ts`, Marek, 2026-09-30): a guest held their phone to one of
 * 2213's coasters and its sticker opened this — a secret neon billboard from the city, full screen,
 * and under it what the room said: +1000 in 2213, nothing in any other era, nothing twice.
 *
 * Someone with no seat on this phone (a coaster taken home, a curious friend) gets the billboard as a
 * teaser for the show instead, with no year on it and the way to book.
 *
 * Neon that breathes, never flickers: the glow comes up once and swells slowly (well under once a
 * second), and the secret line types itself in.
 */
export type SignalResult = 'tuning' | 'found' | 'already' | 'quiet' | 'unknown' | 'teaser';

/** The code a coaster's link carries (`?signal=K7Q2`), taken off the address so a reload is not a second scan. */
export function takeSignal(href = window.location.href): string | null {
  const url = new URL(href);
  const code = url.searchParams.get('signal');
  if (!code) return null;
  url.searchParams.delete('signal');
  try {
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* an address that cannot be changed: the code is still ours */
  }
  return code.trim().toUpperCase().slice(0, 12) || null;
}

/** What the room's refusal of a scan means for the billboard. */
export function signalResultFor(error: string): SignalResult {
  if (error === 'signal already found') return 'already';
  if (error === 'signal quiet') return 'quiet';
  return 'unknown';
}

const WORDS = {
  en: {
    eyebrow: 'HIDDEN CHANNEL · 2213',
    teaserEyebrow: 'SS AURORA · A HIDDEN SIGNAL',
    sign: ['THE CITY THAT', 'NEVER SWITCHES OFF'],
    secret: 'Not everything worth finding is on a screen. You just proved it.',
    tuning: 'TUNING IN…',
    found: '+1000 POINTS',
    foundSub: 'Your find is on the big screen.',
    already: 'You found it already. The points are yours.',
    quiet: 'The signal is quiet now. It only pays in 2213.',
    unknown: 'This signal is not one of ours.',
    teaser: 'An evening 765 years long.',
    book: 'Book your voyage →',
    close: 'BACK TO THE VOYAGE',
  },
  no: {
    eyebrow: 'SKJULT KANAL · 2213',
    teaserEyebrow: 'SS AURORA · ET SKJULT SIGNAL',
    sign: ['BYEN SOM ALDRI', 'SLÅR SEG AV'],
    secret: 'Ikke alt som er verdt å finne, står på en skjerm. Det beviste du nettopp.',
    tuning: 'STILLER INN…',
    found: '+1000 POENG',
    foundSub: 'Funnet ditt vises på storskjermen.',
    already: 'Du har allerede funnet det. Poengene er dine.',
    quiet: 'Signalet er stille nå. Det gir bare poeng i 2213.',
    unknown: 'Dette signalet er ikke vårt.',
    teaser: 'En kveld som varer i 765 år.',
    book: 'Bestill reisen →',
    close: 'TILBAKE TIL REISEN',
  },
} as const;

const PINK = '#ff4fd8';
const CYAN = '#39e1ff';

export function SignalBillboard({ lang, result, onClose }: { lang: Lang; result: SignalResult; onClose?: () => void }) {
  const w = WORDS[lang];
  const id = useId().replace(/:/g, '');
  const teaser = result === 'teaser';
  // The secret line types itself in, a letter at a time, once the sign is lit.
  const [typed, setTyped] = useState(0);
  useEffect(() => {
    let n = 0;
    let timer: ReturnType<typeof setTimeout>;
    const next = () => {
      n += 1;
      setTyped(n);
      if (n < w.secret.length) timer = setTimeout(next, 38);
    };
    timer = setTimeout(next, 1_400);
    return () => clearTimeout(timer);
  }, [w.secret]);

  const verdict =
    result === 'found' ? null : result === 'tuning' ? w.tuning : result === 'already' ? w.already : result === 'quiet' ? w.quiet : result === 'unknown' ? w.unknown : null;

  return (
    <div
      role="dialog"
      aria-label={w.sign.join(' ')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 400,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        padding: 'calc(28px + env(safe-area-inset-top)) 20px calc(28px + env(safe-area-inset-bottom))',
        boxSizing: 'border-box',
        overflow: 'hidden',
        background: 'radial-gradient(120% 70% at 50% 110%, #2a0b3a 0%, #0d0618 55%, #05030a 100%)',
        color: '#f4ecff',
        animation: 'ccFadeIn .5s ease-out',
      }}
    >
      <style>{`
        @keyframes sig-${id}-on { 0% { opacity: 0 } 60% { opacity: .35 } 100% { opacity: 1 } }
        @keyframes sig-${id}-breathe { 0%, 100% { opacity: .82 } 50% { opacity: 1 } }
        @keyframes sig-${id}-rise { 0% { transform: translateY(0) } 100% { transform: translateY(-40px) } }
      `}</style>

      {/* the city below the sign: slow towers of light */}
      <svg viewBox="0 0 400 200" preserveAspectRatio="xMidYMax slice" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', height: '42%', opacity: 0.55 }} aria-hidden="true">
        <defs>
          <linearGradient id={`tower-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={PINK} stopOpacity=".35" />
            <stop offset="1" stopColor="#0d0618" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[
          [10, 90, 34],
          [52, 60, 26],
          [86, 120, 40],
          [134, 40, 30],
          [172, 100, 46],
          [226, 70, 28],
          [262, 130, 38],
          [308, 55, 30],
          [346, 95, 44],
        ].map(([x, top, width], i) => (
          <g key={i}>
            <rect x={x} y={top} width={width} height={200 - top!} fill={`url(#tower-${id})`} />
            {Array.from({ length: 6 }, (_, j) => (
              <rect key={j} x={x! + 5 + ((j * 7) % (width! - 10))} y={top! + 12 + j * 14} width="3" height="2" fill={j % 2 ? CYAN : PINK} opacity=".7" />
            ))}
          </g>
        ))}
      </svg>

      <div style={{ position: 'relative', fontFamily: mono, fontSize: 12, letterSpacing: 3, color: CYAN, opacity: 0.9 }}>
        {teaser ? w.teaserEyebrow : w.eyebrow}
      </div>

      {/* the billboard */}
      <div
        style={{
          position: 'relative',
          width: 'min(100%, 380px)',
          boxSizing: 'border-box',
          padding: '26px 16px 22px',
          borderRadius: 14,
          border: `2px solid ${PINK}`,
          boxShadow: `0 0 18px ${PINK}88, inset 0 0 22px ${PINK}33`,
          background: 'linear-gradient(180deg, rgba(40,8,48,.85), rgba(12,4,22,.9))',
          textAlign: 'center',
          animation: `sig-${id}-on 1.1s ease-out both`,
        }}
      >
        {/* bolts and brackets, so it is a sign hung on a wall */}
        {[
          [8, 8],
          [8, -8],
          [-8, 8],
          [-8, -8],
        ].map(([x, y], i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              [x! > 0 ? 'left' : 'right']: 8,
              [y! > 0 ? 'top' : 'bottom']: 8,
              width: 6,
              height: 6,
              borderRadius: 3,
              background: '#8a6f9a',
            }}
          />
        ))}
        <div style={{ animation: `sig-${id}-breathe 3.6s ease-in-out 1.1s infinite` }}>
          {w.sign.map((line, i) => (
            <div
              key={line}
              style={{
                fontFamily: sans,
                fontSize: i === 0 ? 24 : 30,
                fontWeight: 800,
                letterSpacing: 2,
                lineHeight: 1.08,
                color: i === 0 ? '#ffe6fb' : '#e9fbff',
                textShadow: i === 0 ? `0 0 6px ${PINK}, 0 0 16px ${PINK}, 0 0 28px ${PINK}` : `0 0 6px ${CYAN}, 0 0 16px ${CYAN}, 0 0 28px ${CYAN}`,
              }}
            >
              {line}
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 18,
            minHeight: 48,
            fontFamily: mono,
            fontSize: 15,
            lineHeight: 1.5,
            color: '#f6e7ff',
          }}
        >
          {w.secret.slice(0, typed)}
          <span style={{ opacity: typed < w.secret.length ? 0.8 : 0 }}>▌</span>
        </div>
      </div>

      {/* what the room said */}
      <div style={{ position: 'relative', minHeight: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
        {result === 'found' ? (
          <>
            <div
              style={{
                fontFamily: sans,
                fontSize: 40,
                fontWeight: 800,
                letterSpacing: 2,
                color: '#fff',
                textShadow: `0 0 10px ${CYAN}, 0 0 24px ${CYAN}`,
                animation: 'ccRise .6s ease-out both',
              }}
            >
              {w.found}
            </div>
            <div style={{ fontFamily: mono, fontSize: 13, letterSpacing: 1.5, color: '#cdb7dc' }}>{w.foundSub}</div>
          </>
        ) : teaser ? (
          <>
            <div style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, color: '#fff' }}>{w.teaser}</div>
            <a
              href="https://ss-aurora.no"
              style={{
                marginTop: 6,
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 52,
                padding: '0 22px',
                borderRadius: 12,
                background: PINK,
                color: '#1a0418',
                fontFamily: sans,
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: 2,
                textDecoration: 'none',
              }}
            >
              {w.book}
            </a>
          </>
        ) : (
          <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, color: '#f0e4fb', maxWidth: 320 }}>{verdict}</div>
        )}
      </div>

      {onClose && !teaser ? (
        <button
          onClick={onClose}
          style={{
            position: 'relative',
            width: 'min(100%, 340px)',
            boxSizing: 'border-box',
            minHeight: 56,
            borderRadius: 12,
            border: `1px solid ${CYAN}88`,
            background: 'rgba(57,225,255,.08)',
            color: '#e9fbff',
            fontFamily: sans,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 3,
          }}
        >
          {w.close}
        </button>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ the call to look for it

/**
 * The hidden signal, announced (Marek, 2026-10-06: in 2213 nobody knew there was something to look for).
 * On the clicker's own step for it (2026-10-09: "it should be triggered by the clicker"), every seated
 * phone that has not found it yet gets this once an evening: a neon sign saying a signal is hidden
 * somewhere near them — never where (Marek: "they should not have written directly where the chip is
 * located") — the points, and how to scan a chip on an iPhone and on an Android. Pull it down or GOT IT;
 * when the games open, a small banner reminds whoever has still not found it, and opens this again. Only
 * on the guest's own phone: never on the TV or the table, and it says nothing of who has found it.
 */
export const CALL_WORDS = {
  en: {
    eyebrow: 'HIDDEN SIGNAL · 2213',
    sign: ['A HIDDEN SIGNAL', 'IS ON BOARD'],
    line: 'Somewhere near you, a chip is hidden. Find it, and hold your phone to it:',
    points: (n: number) => `+${n} POINTS`,
    once: 'Once, and only in this era.',
    how: 'HOW TO SCAN',
    iphone: 'iPhone',
    iphoneHow: 'Screen on. Hold the top of the phone flat against the chip, then tap the note that pops up.',
    android: 'Android',
    androidHow: 'NFC on. Hold the middle of the back of the phone against the chip.',
    go: 'GOT IT',
    bannerTitle: 'HIDDEN SIGNAL',
    bannerLine: (n: number) => `A chip is hidden near you: +${n} points. Tap to see how.`,
  },
  no: {
    eyebrow: 'SKJULT SIGNAL · 2213',
    sign: ['ET SKJULT SIGNAL', 'ER OM BORD'],
    line: 'Et sted nær deg er en chip gjemt. Finn den, og hold telefonen mot den:',
    points: (n: number) => `+${n} POENG`,
    once: 'Én gang, og bare i denne tidsalderen.',
    how: 'SLIK SKANNER DU',
    iphone: 'iPhone',
    iphoneHow: 'Skjermen på. Hold toppen av telefonen flatt mot chipen, og trykk på varselet som dukker opp.',
    android: 'Android',
    androidHow: 'NFC på. Hold midten av baksiden av telefonen mot chipen.',
    go: 'SKJØNNER',
    bannerTitle: 'SKJULT SIGNAL',
    bannerLine: (n: number) => `En chip er gjemt nær deg: +${n} poeng. Trykk for å se hvordan.`,
  },
} as const;

/** Where this phone keeps that it has had the call (and the reminder) tonight: per voyage, seat and evening. */
export function signalCallKey(kind: 'call' | 'reminder', voyageId: string, seat: number, evening: string): string {
  return `continuum.signal.${kind}.${voyageId}.${seat}.${evening}`;
}

interface SignalMoment {
  chapter: number;
  phase: string;
  /** Checked in and on a post: the call is for guests at the table. */
  seated: boolean;
  /** The room says this guest has found it already. */
  found: boolean;
}

/** The call: on the clicker's signal step in its era, to a guest who has not found it and not had it tonight. */
export function signalCallDue(m: SignalMoment, seen: boolean): boolean {
  return m.seated && !m.found && !seen && m.chapter === SIGNAL.chapter && m.phase === 'signal';
}

/** The reminder: once the signal's era's games are on, to a guest who has still not found it. */
export function signalReminderDue(m: SignalMoment, seen: boolean): boolean {
  return m.seated && !m.found && !seen && m.chapter === SIGNAL.chapter && m.phase === 'fieldwork';
}

/** A phone, with the spot that reads a chip glowing: the top on an iPhone, the middle of the back on an Android. */
function PhoneSpot({ spot }: { spot: 'top' | 'middle' }) {
  return (
    <svg width="34" height="50" viewBox="0 0 34 50" aria-hidden="true" style={{ flex: 'none' }}>
      <rect x="6" y="2" width="22" height="46" rx="5" fill="rgba(57,225,255,.06)" stroke={CYAN} strokeWidth="2" />
      <circle cx="17" cy={spot === 'top' ? 11 : 25} r="5" fill={PINK} opacity=".9" />
      <circle cx="17" cy={spot === 'top' ? 11 : 25} r="8.5" fill="none" stroke={PINK} strokeWidth="1.5" opacity=".45" />
    </svg>
  );
}

function CallButton({ label }: { label: string }) {
  const { close } = useSheet();
  return (
    <PrimaryButton big bg={PINK} fg="#1a0418" onClick={close}>
      {label}
    </PrimaryButton>
  );
}

export function SignalCall({ lang, points = SIGNAL.points, onAway }: { lang: Lang; points?: number; onAway: () => void }) {
  const w = CALL_WORDS[lang];
  const id = useId().replace(/:/g, '');
  useEffect(() => {
    navigator.vibrate?.([120, 80, 120]);
  }, []);
  return (
    <BottomSheet
      id="signal-call"
      size="full"
      critical
      zIndex={63}
      label={w.sign.join(' ')}
      onDismiss={onAway}
      tone={{ bg: 'radial-gradient(120% 70% at 50% 110%, #2a0b3a 0%, #0d0618 55%, #05030a 100%)', fg: '#f4ecff', border: `${PINK}88`, grip: '#cdb7dc' }}
      footer={<CallButton label={w.go} />}
    >
      <style>{`
        @keyframes sigcall-${id}-on { 0% { opacity: 0 } 60% { opacity: .35 } 100% { opacity: 1 } }
        @keyframes sigcall-${id}-breathe { 0%, 100% { opacity: .82 } 50% { opacity: 1 } }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(7px, 1.6vh, 16px)', textAlign: 'center' }}>
        <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 3, color: CYAN }}>{w.eyebrow}</div>

        {/* the sign, lit once and breathing slowly, never flickering */}
        <div
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: 'clamp(10px, 2.2vh, 20px) 12px',
            borderRadius: 14,
            border: `2px solid ${PINK}`,
            boxShadow: `0 0 18px ${PINK}88, inset 0 0 22px ${PINK}33`,
            background: 'linear-gradient(180deg, rgba(40,8,48,.85), rgba(12,4,22,.9))',
            animation: `sigcall-${id}-on 1.1s ease-out both`,
          }}
        >
          <div style={{ animation: `sigcall-${id}-breathe 3.6s ease-in-out 1.1s infinite` }}>
            {w.sign.map((line, i) => (
              <div
                key={line}
                style={{
                  fontFamily: sans,
                  fontSize: i === 0 ? 'clamp(22px, 4.2vh, 28px)' : 'clamp(24px, 4.6vh, 31px)',
                  fontWeight: 800,
                  letterSpacing: 1.5,
                  lineHeight: 1.08,
                  color: i === 0 ? '#ffe6fb' : '#e9fbff',
                  textShadow: i === 0 ? `0 0 6px ${PINK}, 0 0 16px ${PINK}, 0 0 28px ${PINK}` : `0 0 6px ${CYAN}, 0 0 16px ${CYAN}, 0 0 28px ${CYAN}`,
                }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontFamily: sans, fontSize: 'clamp(16px, 2.9vh, 19px)', fontWeight: 600, lineHeight: 1.3, color: '#f0e4fb' }}>{w.line}</div>
        <div>
          <div
            style={{
              fontFamily: sans,
              fontSize: 'clamp(30px, 6vh, 40px)',
              fontWeight: 800,
              letterSpacing: 2,
              lineHeight: 1,
              color: '#fff',
              textShadow: `0 0 10px ${CYAN}, 0 0 24px ${CYAN}`,
            }}
          >
            {w.points(points)}
          </div>
          <div style={{ marginTop: 6, fontFamily: mono, fontSize: 12, letterSpacing: 1, color: '#cdb7dc' }}>{w.once}</div>
        </div>

        {/* how to scan it, on each kind of phone */}
        <div style={{ width: '100%', textAlign: 'left' }}>
          <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: CYAN }}>{w.how}</div>
          {(
            [
              ['top', w.iphone, w.iphoneHow],
              ['middle', w.android, w.androidHow],
            ] as const
          ).map(([spot, phone, how]) => (
            <div key={phone} style={{ marginTop: 'clamp(6px, 1.2vh, 10px)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <PhoneSpot spot={spot} />
              <div style={{ fontFamily: sans, fontSize: 'clamp(15px, 2.7vh, 17px)', fontWeight: 600, lineHeight: 1.28, color: '#f4ecff' }}>
                <span style={{ fontWeight: 800, color: '#ffe6fb' }}>{phone}: </span>
                {how}
              </div>
            </div>
          ))}
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * While the games are on, for whoever has still not found it: one small neon banner, once. A tap opens the
 * call again; it goes by itself after a while.
 */
export function SignalReminder({
  lang,
  show,
  points = SIGNAL.points,
  onOpen,
  onAway,
}: {
  lang: Lang;
  show: boolean;
  points?: number;
  onOpen: () => void;
  onAway: () => void;
}) {
  const w = CALL_WORDS[lang];
  const node = show ? (
    <TopBanner tone={{ fg: '#ffe6fb', bg: '#1a0826', border: PINK }} autoHideMs={12_000} onDismiss={onAway} onOpen={onOpen}>
      <div style={{ paddingBottom: 6 }}>
        <div
          style={{
            fontFamily: sans,
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 2,
            color: '#ffe6fb',
            textShadow: `0 0 6px ${PINK}, 0 0 14px ${PINK}`,
          }}
        >
          {w.bannerTitle}
        </div>
        <div style={{ marginTop: 4, fontFamily: sans, fontSize: 17, fontWeight: 600, lineHeight: 1.3, color: '#f4ecff' }}>{w.bannerLine(points)}</div>
      </div>
    </TopBanner>
  ) : null;
  useBanner('signal-reminder', PRIORITY.story, node);
  return null;
}
